import 'chart.js/auto';
import BlockUi from '@availity/block-ui';
import type { ActiveElement, ChartEvent, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { CSVLink } from 'react-csv';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { DateInput } from 'semantic-ui-calendar-react-17';
import { Button, Dropdown, Form, Grid, Header, Icon, Label, Radio, Segment } from 'semantic-ui-react';
import { getRidershipAvgByRoute, getRidershipStopsRankingByRoutes, getRidershipSumByRoute } from '../../../actions/ridershipActions';
import { AppState } from '../../../reducers';
import { getSelectedOrDefaultAgency } from '../../../selectors';
import type { RidershipHistoryRecord } from '../../../types/ridershipTypes';
import type { DayTypeFilter } from '../../../utils/dayType';
import { dayTypeOptions, normalizeDayTypeFilter } from '../../../utils/dayType';
import { formatRidershipMonthOrDateRange, getRidershipDateOnlyDaysAgo } from '../../../utils/ridershipDate';
import type { DirectionCatalogOption } from '../average-weekday-by-stop/averageWeekdayByStopDirectionUtils';
import { DirectionSelect } from '../common/RidershipSelectors';
import { projectByAggregation, toRidershipCsvInteger } from '../common/ridershipUtils';
import { resolveStopRankingRows } from './rankingDirectionUtils';
import { useRidershipDirectionCatalog } from './useRidershipDirectionCatalog';


const styles: { [key: string]: React.CSSProperties } = {
    chartDescription: {
        marginBottom: '2em',
    },
    date: {
        display: 'inline-block',
        width: '100%',
    },
    time: {
        display: 'inline-block',
        width: '100%',
    },
    navLink: {
        color: '#337ab7',
        float: 'right',
        fontFamily: '"Segoe UI",Verdana,Helvetica,Sans-Serif',
    },
    months: {
        display: 'block',
        fontSize: '0.9em',
        color: 'rgba(0,0,0,.87)',
        marginBottom: 4,
    },
};

// This report ranks routes and stops by selected metric for Average Weekday Ridership
// Data source and approach mirror AverageWeekdayActivityByStopPage: we call getRidershipHistory
// and aggregate the returned APC-based events.

interface Props {
    agency: ReturnType<typeof getSelectedOrDefaultAgency>;
}

type Metric = 'load' | 'boarding' | 'alighting';
type AggregationMode = 'sum' | 'avg' | 'avg_trips';

// Match colors with AverageWeekdayActivityByStopPage
const metricChartColors: Record<Metric, {
    background: string;
    border: string;
    labelColor: 'green' | 'orange' | 'blue'
}> = {
    boarding: {
        background: 'rgba(46, 204, 113, 0.85)',
        border: '#27ae60',
        labelColor: 'green',
    },
    alighting: {
        background: 'rgba(243, 156, 18, 0.85)',
        border: '#d35400',
        labelColor: 'orange',
    },
    load: {
        background: 'rgba(52, 152, 219, 0.85)',
        border: '#2980b9',
        labelColor: 'blue',
    },
};

const metricOptions: { key: Metric; value: Metric; text: string }[] = [
    {
        key: 'load',
        value: 'load',
        text: 'Load',
    },
    {
        key: 'boarding',
        value: 'boarding',
        text: 'Boardings',
    },
    {
        key: 'alighting',
        value: 'alighting',
        text: 'Alightings',
    },
];

const aggregationOptions = [
    {
        key: 'sum',
        value: 'sum',
        text: 'Sum',
    },
    {
        key: 'avg',
        value: 'avg',
        text: 'Daily Average',
    },
    {
        key: 'avg_trips',
        value: 'avg_trips',
        text: 'Average by Trip',
    },
];

function isAbortError(error: any) {
    return error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
}

function formatNumber(value: number | null | undefined): string {
    const numeric = Number.isFinite(value as number) ? Number(value) : 0;
    return numeric.toLocaleString('en-US');
}

const stopChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
        x: {
            ticks: {
                callback: (value) => formatNumber(typeof value === 'string' ? Number(value) : value as number),
            },
        },
        y: {
            stacked: true,
        },
    },
    plugins: {
        legend: { display: false },
        // datalabels: { display: false },
        datalabels: {
            color: '#333',
            anchor: 'end',
            align: 'right',
            formatter: (v: number, context) => {
                if (context.dataset.order == 1) {
                    return v ? `Adjusted: ${formatNumber(Math.round(v))}` : '';
                }
                return '';
            },
        },
        tooltip: {
            callbacks: {
                label: (ctx) => {
                    const labelPrefix = ctx.dataset?.label ? `${ctx.dataset.label}: ` : '';
                    const value = ctx.parsed?.x ?? ctx.raw ?? 0;
                    return `${labelPrefix}${formatNumber(Math.round(value as number))}`;
                },
            },
        },
    },
};

function toRouteLabel(route: string | number | null | undefined): string {
    if (typeof route === 'number') return route.toString();
    const trimmed = route?.trim();
    return trimmed ? trimmed : 'Unknown';
}

function aggregate<T extends string | number>(rows: RidershipHistoryRecord[], key: (r: RidershipHistoryRecord) => T) {
    const map = new Map<T, {
        label: string;
        load: number;
        boarding: number;
        alighting: number;
        adjustedLoad: number;
        adjustedBoarding: number;
        adjustedAlighting: number
    }>();
    for (const r of rows ?? []) {
        const k = key(r);
        const g = map.get(k) || {
            label: '',
            load: 0,
            boarding: 0,
            alighting: 0,
            adjustedLoad: 0,
            adjustedBoarding: 0,
            adjustedAlighting: 0,
        };

        g.label = g.label || String(k);
        g.load += Math.max(0, Number(r.load) || 0);
        g.boarding += Math.max(0, Number(r.boarding) || 0);
        g.alighting += Math.max(0, Number(r.alighting) || 0);
        g.adjustedLoad += Math.max(0, Number(r.adjustedLoad) || 0);
        g.adjustedBoarding += Math.max(0, Number(r.adjustedBoarding) || 0);
        g.adjustedAlighting += Math.max(0, Number(r.adjustedAlighting) || 0);
        map.set(k, g);
    }
    return map;
}

function toChart(map: Map<any, {
    label: string;
    load: number;
    boarding: number;
    alighting: number
}>, metric: Metric, top?: number) {
    const rows = Array.from(map.entries()).map(([k, v]) => ({ key: k, ...v }));
    rows.sort((a, b) => (b[metric] as number) - (a[metric] as number));
    const slice = typeof top === 'number' && top > 0 ? rows.slice(0, top) : rows;
    const colors = metricChartColors[metric];
    return {
        labels: slice.map(r => r.label),
        datasets: [
            {
                label: metric === 'load' ? 'Load' : metric === 'boarding' ? 'Boardings' : 'Alightings',
                data: slice.map(r => r[metric] as number),
                backgroundColor: colors.background,
                borderColor: colors.border,
                borderWidth: 1,
            },
        ],
    } as any;
}

function toChartWithAdjusted(map: Map<any, {
    label: string;
    load: number;
    boarding: number;
    alighting: number;
    adjustedLoad: number;
    adjustedBoarding: number;
    adjustedAlighting: number;
}>, metric: Metric, top?: number) {

    const rows = Array.from(map.entries()).map(([k, v]) => ({ key: k, ...v }));
    rows.sort((a, b) => (b[metric] as number) - (a[metric] as number));
    const slice = typeof top === 'number' && top > 0 ? rows.slice(0, top) : rows;
    const colors = metricChartColors[metric];
    let adjustedKey: 'adjustedLoad' | 'adjustedBoarding' | 'adjustedAlighting';
    if (metric === 'load') {
        adjustedKey = 'adjustedLoad';
    } else {
        if (metric === 'boarding') {
            adjustedKey = 'adjustedBoarding';
        } else {
            adjustedKey = 'adjustedAlighting';
        }
    }
    const baseValues = slice.map(r => r[metric] as number);
    const adjustedValues = slice.map(r => (r[adjustedKey] as number) || 0);

    return {
        labels: slice.map(r => r.label),
        datasets: [
            {
                label: metric === 'load' ? 'Load' : metric === 'boarding' ? 'Boardings' : 'Alightings',
                data: baseValues,
                backgroundColor: colors.background,
                borderColor: colors.border,
                borderWidth: 1,
                // stack: 'ridership',
                categoryPercentage: 0.8,
                order: 0,
            },
            {
                label: 'Adjusted',
                data: adjustedValues,
                backgroundColor: 'rgba(160, 175, 160, 0.6)',
                borderColor: 'rgba(160, 175, 160, 0.9)',
                borderWidth: 1,
                // stack: 'ridership',
                categoryPercentage: 1,
                order: 1,
            },
        ],
    } as any;
}

function toStopChartLabel(row: RidershipHistoryRecord): string {
    return String(`${row.stopName} (${String(row.stopId)})` ?? 'Unknown');
}

function toDirectionFileSlug(direction: DirectionCatalogOption | null): string {
    if (!direction?.displayName) {
        return '';
    }

    return `_${direction.displayName.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '')}`;
}

const RidershipRankingPage: React.FC<Props> = (props) => {
    const [metric, setMetric] = React.useState<Metric>('alighting');
    const [aggregation, setAggregation] = React.useState<AggregationMode>('sum');
    const [routeLoading, setRouteLoading] = React.useState(false);
    const [stopLoading, setStopLoading] = React.useState(false);
    const [routeRowsSum, setRouteRowsSum] = React.useState<RidershipHistoryRecord[] | null>(null);
    const [stopRowsSum, setStopRowsSum] = React.useState<RidershipHistoryRecord[] | null>(null);
    const [selectedRoute, setSelectedRoute] = React.useState<string | null>(null);
    const [selectedDirection, setSelectedDirection] = React.useState<DirectionCatalogOption | null>(null);
    const [monthsFilterState, setMonthsFilterState] = React.useState<string[]>([]);
    const routeAbortRef = React.useRef<AbortController | null>(null);
    const stopAbortRef = React.useRef<AbortController | null>(null);
    const routeCsvLinkRef = React.useRef<any>(null);
    const stopCsvLinkRef = React.useRef<any>(null);
    const [appliedFilters, setAppliedFilters] = React.useState<{
        fromDate: string;
        toDate: string;
        dayType: DayTypeFilter;
        aggregation: AggregationMode;
    } | null>(null);

    const catalogFilters = React.useMemo(() => {
        if (!appliedFilters) {
            return null;
        }

        return {
            fromDate: appliedFilters.fromDate,
            toDate: appliedFilters.toDate,
        };
    }, [appliedFilters]);

    const { catalogLoading, getDirectionsForRoute } = useRidershipDirectionCatalog(props.agency?.id, catalogFilters);
    const directionOptions = React.useMemo(
        () => getDirectionsForRoute(selectedRoute),
        [getDirectionsForRoute, selectedRoute],
    );

    const startAbortableRequest = React.useCallback((ref: React.MutableRefObject<AbortController | null>) => {
        if (ref.current) {
            ref.current.abort();
        }
        const controller = new AbortController();
        ref.current = controller;
        return controller;
    }, []);

    const reportLoading = routeLoading || stopLoading;

    const handleCancel = React.useCallback(() => {
        routeAbortRef.current?.abort();
        stopAbortRef.current?.abort();
        setRouteLoading(false);
        setStopLoading(false);
    }, []);

    // Date/day-type filters with defaults: last 30 days, Workdays.
    const [fromDate, setFromDate] = React.useState<string>(getRidershipDateOnlyDaysAgo(30));
    const [toDate, setToDate] = React.useState<string>(getRidershipDateOnlyDaysAgo(0));
    const [dayType, setDayType] = React.useState<DayTypeFilter>('workdays');

    const setMonthsFilter = React.useCallback(() => {
        const months: string[] = [];
        const endDate = moment().startOf('day');
        let startDate = moment().add(-11, 'months').startOf('day');
        while (startDate.isSameOrBefore(endDate)) {
            const date = startDate.format('YYYY-MM-DD');
            months.push(date);
            startDate = startDate.add(1, 'months');
        }
        setMonthsFilterState(months);
    }, []);

    React.useEffect(() => {
        setMonthsFilter();
    }, [setMonthsFilter]);

    const handleApply = React.useCallback((filters?: { fromDate: string; toDate: string; dayType: DayTypeFilter; aggregation: AggregationMode }) => {
        setAppliedFilters(filters ?? {
            fromDate,
            toDate,
            dayType: normalizeDayTypeFilter(dayType),
            aggregation,
        });
        setSelectedRoute(null);
        setSelectedDirection(null);
        setRouteRowsSum(null);
        setStopRowsSum(null);
    }, [aggregation, fromDate, toDate, dayType]);

    const handleMonthFilterClick = React.useCallback((date: string) => {
        const start = moment(date).startOf('month').format('YYYY-MM-DD');
        const end = moment(date).endOf('month').format('YYYY-MM-DD');
        setFromDate(start);
        setToDate(end);
        handleApply({
            fromDate: start,
            toDate: end,
            dayType: normalizeDayTypeFilter(dayType as DayTypeFilter),
            aggregation,
        });
    }, [aggregation, dayType, handleApply]);

    const dayTypeOptionsLocal = dayTypeOptions;

    React.useEffect(() => {
        if (!props.agency?.id) {
            setAppliedFilters(null);
            setSelectedRoute(null);
            setSelectedDirection(null);
            setRouteRowsSum(null);
            setStopRowsSum(null);
        }
    }, [props.agency?.id]);

    React.useEffect(() => {
        const agencyId = props.agency?.id;
        if (!agencyId || !appliedFilters) {
            setRouteLoading(false);
            setRouteRowsSum(null);
            return;
        }
        let cancelled = false;
        setRouteRowsSum(null);
        const controller = startAbortableRequest(routeAbortRef);
        (async () => {
            try {
                setRouteLoading(true);
                const routeRequest = appliedFilters.aggregation === 'sum'
                    ? getRidershipSumByRoute
                    : getRidershipAvgByRoute;
                const routesSum = await routeRequest(agencyId, appliedFilters.fromDate, appliedFilters.toDate, appliedFilters.dayType, { signal: controller.signal });
                if (cancelled || controller.signal.aborted) return;
                setRouteRowsSum(routesSum || []);
            } catch (error) {
                if (isAbortError(error) || cancelled || controller.signal.aborted) {
                    return;
                }
                throw error;
            } finally {
                if (!cancelled) setRouteLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [props.agency?.id, appliedFilters, startAbortableRequest]);

    React.useEffect(() => {
        const agencyId = props.agency?.id;
        if (!agencyId || !appliedFilters || !selectedRoute) {
            setStopLoading(false);
            setStopRowsSum(null);
            return;
        }

        let cancelled = false;
        setStopRowsSum(null);
        const controller = startAbortableRequest(stopAbortRef);
        (async () => {
            try {
                setStopLoading(true);
                const requestAggregation = appliedFilters.aggregation === 'sum' ? 'sum' : 'avg';
                const stops = await getRidershipStopsRankingByRoutes(agencyId, [selectedRoute], appliedFilters.fromDate, appliedFilters.toDate, appliedFilters.dayType, requestAggregation, { signal: controller.signal });
                if (cancelled || controller.signal.aborted) return;
                setStopRowsSum(stops || []);
            } catch (error) {
                if (isAbortError(error) || cancelled || controller.signal.aborted) {
                    return;
                }
                throw error;
            } finally {
                if (!cancelled) setStopLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [props.agency?.id, appliedFilters, selectedRoute, startAbortableRequest]);

    const appliedAggregation = appliedFilters?.aggregation ?? 'sum';

    const routeRows = React.useMemo(() => projectByAggregation(routeRowsSum, appliedAggregation), [routeRowsSum, appliedAggregation]);

    const stopRows = React.useMemo(() => projectByAggregation(stopRowsSum, appliedAggregation), [stopRowsSum, appliedAggregation]);

    const routeScopedStopRows = React.useMemo(() => {
        if (!selectedRoute) {
            return [];
        }
        return (stopRows || []).filter(r => toRouteLabel(r.routeId ?? r.routeShortName ?? null) === selectedRoute);
    }, [stopRows, selectedRoute]);

    const directionScopedStopRows = React.useMemo(
        () => resolveStopRankingRows(routeScopedStopRows, selectedDirection),
        [routeScopedStopRows, selectedDirection],
    );

    const routeAgg = React.useMemo(() => aggregate(routeRows || [], r => toRouteLabel(r.routeId ?? r.routeShortName ?? null)), [routeRows]);
    const stopAgg = React.useMemo(() => aggregate(directionScopedStopRows || [], toStopChartLabel), [directionScopedStopRows]);

    // Use adjusted chart for ROUTES (adjusted values available only for routes)
    const routeBarData = React.useMemo(() => toChartWithAdjusted(routeAgg, metric), [routeAgg, metric]);
    const stopBarData = React.useMemo(() => toChart(stopAgg, metric), [stopAgg, metric]);

    const metricLabel = React.useMemo(() => (
        metric === 'load' ? 'Load' : metric === 'boarding' ? 'Boardings' : 'Alightings'
    ), [metric]);

    const exportPeriodLabel = React.useMemo(() => {
        if (!appliedFilters) {
            return '';
        }

        return formatRidershipMonthOrDateRange(appliedFilters.fromDate, appliedFilters.toDate);
    }, [appliedFilters]);

    const routeCsvHeaders = React.useMemo(() => ([
        {
            label: 'Route',
            key: 'route',
        },
        {
            label: 'Month / Date Range',
            key: 'period',
        },
        {
            label: metricLabel,
            key: 'value',
        },
        {
            label: `Adjusted ${metricLabel}`,
            key: 'adjusted',
        },
    ]), [metricLabel]);

    const stopCsvHeaders = React.useMemo(() => ([
        {
            label: 'Stop',
            key: 'stop',
        },
        {
            label: 'Month / Date Range',
            key: 'period',
        },
        {
            label: metricLabel,
            key: 'value',
        },
    ]), [metricLabel]);

    const routeCsvRows = React.useMemo(() => {
        const labels = routeBarData.labels || [];
        const baseData = routeBarData.datasets?.[0]?.data as Array<number | null | undefined> | undefined;
        const adjustedData = routeBarData.datasets?.[1]?.data as Array<number | null | undefined> | undefined;
        return labels.map((label: any, index: number) => ({
            route: String(label ?? ''),
            period: exportPeriodLabel,
            value: toRidershipCsvInteger(baseData?.[index] ?? undefined),
            adjusted: toRidershipCsvInteger(adjustedData?.[index] ?? undefined),
        }));
    }, [routeBarData.datasets, routeBarData.labels, exportPeriodLabel]);

    const stopCsvRows = React.useMemo(() => {
        const labels = stopBarData.labels || [];
        const baseData = stopBarData.datasets?.[0]?.data as Array<number | null | undefined> | undefined;
        return labels.map((label: any, index: number) => ({
            stop: String(label ?? ''),
            period: exportPeriodLabel,
            value: toRidershipCsvInteger(baseData?.[index] ?? undefined),
        }));
    }, [stopBarData.datasets, stopBarData.labels, exportPeriodLabel]);

    const routeCsvFileName = React.useMemo(() => {
        const timestamp = moment(new Date()).format('YYYY_MM_DD');
        return `ridership_ranking_routes_${metric}_${appliedAggregation}_${timestamp}.csv`;
    }, [metric, appliedAggregation]);

    const stopCsvFileName = React.useMemo(() => {
        const timestamp = moment(new Date()).format('YYYY_MM_DD');
        const routePart = selectedRoute ? `_route_${selectedRoute}` : '';
        const directionPart = selectedDirection ? toDirectionFileSlug(selectedDirection) : '';
        return `ridership_ranking_stops_${metric}_${appliedAggregation}${routePart}${directionPart}_${timestamp}.csv`;
    }, [metric, appliedAggregation, selectedRoute, selectedDirection]);

    const routeCsvDisabled = routeLoading || !routeCsvRows.length;
    const stopCsvDisabled = stopLoading || !stopCsvRows.length;

    React.useEffect(() => {
        const labels = (routeBarData.labels || [])
            .map((label: any) => String(label ?? '').trim())
            .filter((label: string) => label.length > 0);
        if (labels.length === 0) {
            if (selectedRoute !== null) {
                setSelectedRoute(null);
            }
            return;
        }
        if (!selectedRoute || !labels.includes(selectedRoute)) {
            setSelectedRoute(labels[0]);
        }
    }, [routeBarData.labels, selectedRoute]);

    // Dynamic heights so that many bars can be scrolled within a fixed viewport
    const routeBarContentHeight = React.useMemo(() => Math.max(560, (routeBarData.labels?.length || 0) * 28 + 40), [routeBarData]);
    const stopBarContentHeight = React.useMemo(() => Math.max(560, (stopBarData.labels?.length || 0) * 28 + 40), [stopBarData]);

    const handleRouteBarClick = React.useCallback((_event: ChartEvent, elements: ActiveElement[]) => {
        if (!elements?.length) {
            return;
        }

        const index = elements[0].index;
        const label = typeof index === 'number' ? routeBarData.labels?.[index] ?? null : null;
        if (label) {
            setSelectedRoute(String(label));
            setSelectedDirection(null);
        }
    }, [routeBarData]);

    const routeChartOptions = React.useMemo<ChartOptions<'bar'>>(() => ({
        ...stopChartOptions,
        plugins: {
            ...stopChartOptions.plugins,
            legend: { display: false },
            tooltip: {
                mode: 'y',
                intersect: false,
                callbacks: {
                    label: (ctx) => {
                        const datasetLabel = ctx.dataset?.label ? `${ctx.dataset.label}: ` : '';
                        const value = ctx.parsed?.x ?? ctx.raw ?? 0;
                        return `${datasetLabel}${formatNumber(Math.round(value as number))}`;
                    },
                },
            },
        },
        onHover: (event, elements) => {
            const target = (event?.native as MouseEvent | undefined)?.target as HTMLCanvasElement | undefined;
            if (target) {
                target.style.cursor = elements?.length ? 'pointer' : 'default';
            }
        },
        onClick: handleRouteBarClick,
    }), [handleRouteBarClick]);

    const handleFromDateChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value }: { value: string }) => {
        setFromDate(value);
    };

    const handleToDateChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value }: { value: string }) => {
        setToDate(value);
    };


    return (
        <Segment basic>
            <Header as="h1" className="reportHeader">Ranking Dashboard</Header>
            <div style={styles.chartDescription}>
                Dynamic report ranking routes and stops by selected criteria for Average Weekday Ridership (boardings,
                alightings, and passenger load). This reflects activity captured by APCs aboard buses.
            </div>
            <Form>
                <Grid>
                    <Grid.Row>
                        <Grid.Column width={3}>
                            <Form.Field>
                                <label className="inputLabel">Metric</label>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 16,
                                    paddingTop: 6,
                                    alignItems: 'flex-start',
                                }}>
                                    {metricOptions.map(o => (
                                        <div key={o.key} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                        }}>
                                            <Label color={metricChartColors[o.value].labelColor} empty circular size={'big'}/>
                                            <Radio label={o.text} checked={metric === o.value}
                                                   onChange={() => setMetric(o.value)}/>
                                        </div>
                                    ))}
                                </div>
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column width={13}>
                            <Form.Group widths="equal">
                                <Form.Field className="calendarField">
                                    <label className="calendarInputLabel">From date</label>
                                    <DateInput className="bph-date"
                                               name="averageWeekdayFromDate"
                                               dateFormat="YYYY-MM-DD"
                                               placeholder="Select date"
                                               value={fromDate}
                                               iconPosition="left"
                                               popupPosition="bottom center"
                                               closable={true}
                                               startMode="day"
                                               preserveViewMode={false}
                                               animation="fade"
                                               disableMinute={false}
                                               style={styles.date}
                                               onChange={handleFromDateChange}
                                               maxDate={new Date()}
                                    />
                                </Form.Field>
                                <Form.Field className="calendarField">
                                    <label className="calendarInputLabel">To date</label>
                                    <DateInput className="bph-date"
                                               name="averageWeekdayToDate"
                                               dateFormat="YYYY-MM-DD"
                                               placeholder="Select date"
                                               value={toDate}
                                               iconPosition="left"
                                               popupPosition="bottom center"
                                               closable={true}
                                               startMode="day"
                                               preserveViewMode={false}
                                               animation="fade"
                                               disableMinute={false}
                                               style={styles.date}
                                               onChange={handleToDateChange}
                                               maxDate={new Date()}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <label className="inputLabel">Day Type</label>
                                    <Dropdown className="ui dropdown"
                                              selection
                                              options={dayTypeOptionsLocal}
                                              value={dayType}
                                              onChange={(_, d) => setDayType(normalizeDayTypeFilter(d.value as any))}/>
                                </Form.Field>
                                <Form.Field>
                                    <label className="inputLabel">Aggregation</label>
                                    <Dropdown
                                        className="ui dropdown"
                                        selection
                                        options={aggregationOptions}
                                        value={aggregation}
                                        onChange={(_, d) => setAggregation((d.value as AggregationMode) || 'sum')}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    {reportLoading
                                        ? (
                                            <Button className="fieldControl"
                                                    color="red"
                                                    content="Cancel"
                                                    onClick={handleCancel}/>
                                        )
                                        : (
                                            <Button className="primaryButton fieldControl"
                                                    content="Apply"
                                                    onClick={() => handleApply()}
                                                    disabled={!props.agency?.id}/>
                                        )}
                                </Form.Field>
                            </Form.Group>
                            {monthsFilterState.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <label style={styles.months}>Or choose month:</label>
                                    {monthsFilterState.map((date, index) => (
                                        <Link key={index} to="#" onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                            e.preventDefault();
                                            handleMonthFilterClick(date);
                                        }} style={{ marginRight: 8, fontSize: '0.9em' }}>
                                            {moment(date).format('MMM-YY').toUpperCase()}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </Grid.Column>
                    </Grid.Row>
                </Grid>

            </Form>

            <Grid stackable columns={2} style={{ marginTop: 20 }}>
                <Grid.Row>
                    <Grid.Column>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 10,
                        }}>
                            <Header as="h3" style={{ margin: 0 }}>
                                Route Ranking
                                <Header.Subheader>Click a bar to focus Stop Ranking on that route</Header.Subheader>
                            </Header>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}>
                                <CSVLink
                                    headers={routeCsvHeaders}
                                    data={routeCsvRows}
                                    filename={routeCsvFileName}
                                    target="_blank"
                                    className="hidden"
                                    ref={routeCsvLinkRef as any}
                                />
                                <Button
                                    basic
                                    color="grey"
                                    size="small"
                                    icon
                                    labelPosition="left"
                                    loading={routeLoading}
                                    disabled={routeCsvDisabled}
                                    onClick={() => {
                                        routeCsvLinkRef.current?.link?.click();
                                    }}
                                >
                                    <Icon name="download"/>
                                    Download CSV
                                </Button>
                            </div>
                        </div>
                        <BlockUi blocking={routeLoading} message="Loading route rankings...">
                            <div style={{
                                height: 800,
                                overflowY: 'auto',
                            }}>
                            <div style={{ height: routeBarContentHeight }}>
                                <Bar data={routeBarData}
                                     options={routeChartOptions}
                                     plugins={[ChartDataLabels]}
                                />
                            </div>
                            </div>
                        </BlockUi>
                    </Grid.Column>
                    <Grid.Column>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 10,
                        }}>
                            <Header as="h3" style={{ margin: 0 }}>
                                Stop Ranking
                                <Header.Subheader>
                                    {selectedRoute
                                        ? (
                                            <>
                                                Showing stops for route <strong>{selectedRoute}</strong>
                                                {selectedDirection?.displayName
                                                    ? <> • <strong>{selectedDirection.displayName}</strong></>
                                                    : null}
                                            </>
                                        )
                                        : 'Select route in Route Ranking'}
                                </Header.Subheader>
                            </Header>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}>
                                <CSVLink
                                    headers={stopCsvHeaders}
                                    data={stopCsvRows}
                                    filename={stopCsvFileName}
                                    target="_blank"
                                    className="hidden"
                                    ref={stopCsvLinkRef as any}
                                />
                                <Button
                                    basic
                                    color="grey"
                                    size="small"
                                    icon
                                    labelPosition="left"
                                    loading={stopLoading}
                                    disabled={stopCsvDisabled}
                                    onClick={() => {
                                        stopCsvLinkRef.current?.link?.click();
                                    }}
                                >
                                    <Icon name="download"/>
                                    Download CSV
                                </Button>
                            </div>
                        </div>
                        {selectedRoute && (
                            <Form.Field style={{ marginBottom: 12 }}>
                                <label className="inputLabel">Direction</label>
                                <DirectionSelect
                                    items={directionOptions}
                                    value={selectedDirection}
                                    onChanged={setSelectedDirection}
                                    loading={catalogLoading}
                                    disabled={catalogLoading || directionOptions.length === 0}
                                    clearable
                                    placeholder={directionOptions.length === 0 ? 'No directions available' : 'All directions'}
                                />
                            </Form.Field>
                        )}
                        <BlockUi blocking={stopLoading} message="Loading stop rankings...">
                            <div style={{
                                height: 800,
                                overflowY: 'auto',
                            }}>
                            <div style={{ height: stopBarContentHeight }}>
                                <Bar data={stopBarData} options={stopChartOptions} plugins={[ChartDataLabels]}/>
                            </div>
                            </div>
                        </BlockUi>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </Segment>
    );
};

function mapStateToProps(state: AppState): Props {
    return {
        agency: getSelectedOrDefaultAgency(state),
    } as Props;
}

export default connect(mapStateToProps)(RidershipRankingPage);
